import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import { LocalNotifications } from '@capacitor/local-notifications';

const DAILY_REMINDER_ID = 1;
const DEFAULT_NOTIFICATION_TIME = '07:00';

type NotificationSyncPreferences = {
  notificationsEnabled?: boolean | null;
  pushNotificationsEnabled?: boolean | null;
  notificationTime?: string | null;
};

type NotificationSyncOptions = {
  allowPermissionPrompt?: boolean;
  requestPushRegistration?: boolean;
};

let webReminderTimeoutId: number | null = null;
let webReminderIntervalId: number | null = null;

const isWeb = () => Capacitor.getPlatform() === 'web';

const clearWebReminderTimers = () => {
  if (webReminderTimeoutId !== null) {
    window.clearTimeout(webReminderTimeoutId);
    webReminderTimeoutId = null;
  }

  if (webReminderIntervalId !== null) {
    window.clearInterval(webReminderIntervalId);
    webReminderIntervalId = null;
  }
};

const normalizeClockPart = (value: string, fallback: string) =>
  value.trim() ? value.trim().padStart(2, '0') : fallback;

export const normalizeNotificationTime = (time?: string | null) => {
  if (!time) return DEFAULT_NOTIFICATION_TIME;

  const [rawHour = '07', rawMinute = '00'] = time.split(':');
  return `${normalizeClockPart(rawHour, '07')}:${normalizeClockPart(rawMinute, '00')}`;
};

export const parseNotificationTime = (time?: string | null) => {
  const normalizedTime = normalizeNotificationTime(time);
  const [rawHour = '07', rawMinute = '00'] = normalizedTime.split(':');
  const hour = Math.min(23, Math.max(0, Number(rawHour) || 7));
  const minute = Math.min(59, Math.max(0, Number(rawMinute) || 0));

  return { hour, minute, normalizedTime: `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}` };
};

const shouldScheduleReminder = ({ notificationsEnabled, pushNotificationsEnabled }: NotificationSyncPreferences) =>
  Boolean(notificationsEnabled) && Boolean(pushNotificationsEnabled);

const hasWebNotificationSupport = () => typeof window !== 'undefined' && 'Notification' in window;

const ensureWebNotificationPermission = async (allowPrompt: boolean) => {
  if (!hasWebNotificationSupport()) return false;
  if (Notification.permission === 'granted') return true;
  if (!allowPrompt || Notification.permission === 'denied') return false;

  return (await Notification.requestPermission()) === 'granted';
};

const scheduleWebDailyNotification = async (hour: number, minute: number) => {
  clearWebReminderTimers();

  const showReminder = () => {
    new Notification('Track your observations', {
      body: 'Don\'t forget to log your fertility observations today!',
      tag: 'daily-reminder',
    });
  };

  const now = new Date();
  const next = new Date();
  next.setHours(hour, minute, 0, 0);

  if (next <= now) {
    next.setDate(next.getDate() + 1);
  }

  const delay = next.getTime() - now.getTime();

  webReminderTimeoutId = window.setTimeout(() => {
    showReminder();
    webReminderIntervalId = window.setInterval(showReminder, 24 * 60 * 60 * 1000);
  }, delay);
};

export const registerPushNotifications = async () => {
  if (isWeb()) {
    const granted = await ensureWebNotificationPermission(true);
    if (!granted) {
      throw new Error('Web notification permission was not granted.');
    }

    return;
  }

  const permission = await PushNotifications.requestPermissions();
  if (permission.receive !== 'granted') {
    throw new Error('Push notification permission was not granted.');
  }
  await PushNotifications.register();
};

export const ensureLocalNotificationPermission = async () => {
  if (!Capacitor.isPluginAvailable('LocalNotifications')) {
    return false;
  }

  const current = await LocalNotifications.checkPermissions();
  if (current.display === 'granted') {
    return true;
  }

  const requested = await LocalNotifications.requestPermissions();
  return requested.display === 'granted';
};

export const scheduleDailyNotification = async (hour: number, minute: number) => {
  if (isWeb()) {
    const hasPermission = await ensureWebNotificationPermission(false);
    if (!hasPermission) {
      throw new Error('Web notification permission was not granted.');
    }

    await scheduleWebDailyNotification(hour, minute);
    return;
  }

  const hasPermission = await ensureLocalNotificationPermission();
  if (!hasPermission) {
    throw new Error('Local notification permission was not granted.');
  }

  // Keep a single daily reminder and replace prior schedule when settings change.
  await LocalNotifications.cancel({ notifications: [{ id: DAILY_REMINDER_ID }] });

  await LocalNotifications.schedule({
    notifications: [
      {
        title: 'Track your observations',
        body: 'Don’t forget to log your fertility observations today!',
        id: DAILY_REMINDER_ID,
        schedule: {
          // Uses the device's local timezone and naturally follows DST transitions.
          on: { hour, minute },
          repeats: true,
          allowWhileIdle: true,
        },
      },
    ],
  });
};

export const cancelDailyNotification = async () => {
  clearWebReminderTimers();

  if (!Capacitor.isPluginAvailable('LocalNotifications')) {
    return;
  }

  await LocalNotifications.cancel({ notifications: [{ id: DAILY_REMINDER_ID }] });
};

export const syncDailyReminderNotifications = async (
  preferences: NotificationSyncPreferences,
  options: NotificationSyncOptions = {}
) => {
  const { allowPermissionPrompt = false } = options;
  const { hour, minute } = parseNotificationTime(preferences.notificationTime);

  if (!shouldScheduleReminder(preferences)) {
    await cancelDailyNotification();
    return { scheduled: false };
  }

  if (isWeb()) {
    const hasWebPermission = await ensureWebNotificationPermission(allowPermissionPrompt);
    if (!hasWebPermission) {
      await cancelDailyNotification();
      return { scheduled: false, permissionGranted: false };
    }

    await scheduleWebDailyNotification(hour, minute);
    return { scheduled: true, permissionGranted: true };
  }

  // The daily reminder is a LOCAL notification scheduled on-device, so it only
  // needs LocalNotifications permission — no remote push / APNs registration.
  // (Registering for APNs here previously threw on devices without push set up
  // and prevented the local reminder from ever being scheduled.)
  const hasPermission = allowPermissionPrompt
    ? await ensureLocalNotificationPermission()
    : (await LocalNotifications.checkPermissions()).display === 'granted';

  if (!hasPermission) {
    await cancelDailyNotification();
    return { scheduled: false, permissionGranted: false };
  }

  await scheduleDailyNotification(hour, minute);
  return { scheduled: true, permissionGranted: true };
};
